import { useState } from "react";
import { toast } from "sonner";
import { API, api } from "@/api";
import { Label } from "@/components/ui/label";
import { Upload, FileText, Loader2 } from "lucide-react";

// Resolve a stored value to a displayable URL: legacy base64 data URLs pass through,
// object-storage paths are served via the authenticated backend endpoint.
export const fileUrl = (v) => {
  if (!v) return "";
  if (v.startsWith("data:") || v.startsWith("http")) return v;
  return `${API}/files/${v}`;
};

export const FileUploadField = ({ label, docType, value, onChange, accept = "image/*,application/pdf", image = false, testid }) => {
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) return toast.error("File must be under 3MB");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/uploads/party-doc?doc_type=${docType}`, fd);
      onChange(r.data.path);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">{label}</Label>
      <div className="flex items-center gap-3 flex-wrap">
        {image && (
          value
            ? <img src={fileUrl(value)} alt="" className="w-11 h-11 rounded-xl object-cover border border-slate-300" data-testid={`${testid}-preview`} />
            : <div className="w-11 h-11 rounded-xl bg-slate-100 border border-slate-300" data-testid={`${testid}-empty`} />
        )}
        {!image && value && (
          <a href={fileUrl(value)} target="_blank" rel="noreferrer" data-testid={`${testid}-view`}
            className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-800 px-2.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 font-semibold transition-colors duration-150">
            <FileText className="w-3.5 h-3.5" /> View
          </a>
        )}
        <label data-testid={testid}
          className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 transition-colors duration-150 ${busy ? "bg-slate-200 text-slate-500 cursor-wait" : "bg-emerald-500 text-slate-950 hover:bg-emerald-400 cursor-pointer"}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {busy ? "Uploading…" : value ? "Replace" : "Upload"}
          <input type="file" accept={accept} className="hidden" disabled={busy} onChange={pick} data-testid={`${testid}-input`} />
        </label>
        {value && !busy && (
          <button type="button" onClick={() => onChange("")} data-testid={`${testid}-clear`}
            className="text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-red-500/10 font-semibold transition-colors duration-150">
            Remove
          </button>
        )}
      </div>
    </div>
  );
};
